import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { toast } from "sonner";
import { Pencil, Trash2, Plus, X } from "lucide-react";
import AdminJobs from "@/components/AdminJobs";
import AdminCoupons from "@/components/AdminCoupons";
import AdminIntegrations from "@/components/AdminIntegrations";
import AdminCategories from "@/components/AdminCategories";
import AdminVisitorStats from "@/components/AdminVisitorStats";
import AdminBackup from "@/components/AdminBackup";
import AdminNotifications from "@/components/AdminNotifications";
import ImageUploader from "@/components/ImageUploader";
import OrderDetailModal from "@/components/OrderDetailModal";

const EMPTY_PRODUCT = {
  name_el: "", name_en: "",
  description_el: "", description_en: "",
  price: 0, sale_price: null,
  category_slug: "skylos",
  images: [],
  stock: 100, brand: "", sku: "",
  featured: false, source: "custom", external_id: "",
};

const Admin = () => {
  const { user, loading } = useAuth();
  const { t } = useLang();
  const [tab, setTab] = useState("products");
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [orders, setOrders] = useState([]);
  const [openOrderId, setOpenOrderId] = useState(null);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState(EMPTY_PRODUCT);
  const [maintenance, setMaintenance] = useState(false);
  const [maintLoading, setMaintLoading] = useState(false);

  const loadProducts = () => api.get("/products?limit=200").then((r) => setProducts(r.data.items || []));
  const loadCats = () => api.get("/categories").then((r) => setCategories(r.data || []));
  const loadOrders = () => api.get("/orders").then((r) => setOrders(r.data || []));
  const loadMaintenance = () =>
    api.get("/settings/public").then((r) => setMaintenance(!!r.data?.maintenance_mode));

  useEffect(() => {
    if (user?.role === "admin") {
      loadProducts();
      loadCats();
      loadOrders();
      loadMaintenance();
    }
  }, [user]);

  const toggleMaintenance = async () => {
    setMaintLoading(true);
    try {
      const res = await api.put("/admin/settings/maintenance", { enabled: !maintenance });
      setMaintenance(!!res.data?.maintenance_mode);
      toast.success(
        res.data?.maintenance_mode
          ? t("admin.maintenanceOn")
          : t("admin.maintenanceOff")
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally {
      setMaintLoading(false);
    }
  };

  if (loading) return <div className="max-w-[1300px] mx-auto px-6 py-12">Loading...</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "admin") return <div className="max-w-[1300px] mx-auto px-6 py-12">Forbidden.</div>;

  const openEdit = (p) => {
    setEditing(p?.id || "new");
    setFormData(p ? { ...p, images: p.images || [], external_id: p.external_id || "" } : EMPTY_PRODUCT);
  };

  const closeEdit = () => { setEditing(null); setFormData(EMPTY_PRODUCT); };

  const onChange = (k) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setFormData((s) => ({ ...s, [k]: v }));
  };

  const save = async () => {
    try {
      const payload = {
        ...formData,
        price: parseFloat(formData.price),
        sale_price: formData.sale_price ? parseFloat(formData.sale_price) : null,
        stock: parseInt(formData.stock || 0),
        markup_percent:
          formData.markup_percent === "" || formData.markup_percent == null
            ? null
            : parseFloat(formData.markup_percent),
        images: typeof formData.images === "string"
          ? formData.images.split(",").map((s) => s.trim()).filter(Boolean)
          : formData.images,
      };
      if (editing === "new") {
        await api.post("/products", payload);
        toast.success("Product created");
      } else {
        // When updating an existing product with markup_percent change, omit price so backend recomputes
        if (
          payload.markup_percent != null &&
          formData.cost_price != null &&
          parseFloat(formData.price) ===
            Math.round(parseFloat(formData.cost_price) * (1 + payload.markup_percent / 100) * 100) / 100
        ) {
          delete payload.price;
        }
        await api.patch(`/products/${editing}`, payload);
        toast.success("Updated");
      }
      closeEdit();
      loadProducts();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    await api.delete(`/products/${id}`);
    toast.success("Deleted");
    loadProducts();
  };

  const cancelOrder = async (id) => {
    if (!window.confirm("Ακύρωση αυτής της παραγγελίας; (Δεν διαγράφεται)")) return;
    try {
      await api.patch(`/orders/${id}`, { order_status: "cancelled", payment_status: "cancelled" });
      toast.success("Η παραγγελία ακυρώθηκε");
      loadOrders();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Σφάλμα");
    }
  };

  const deleteOrder = async (id) => {
    if (!window.confirm("Διαγραφή παραγγελίας ΟΡΙΣΤΙΚΑ; Δεν αναιρείται.")) return;
    try {
      await api.delete(`/orders/${id}`);
      toast.success("Η παραγγελία διαγράφηκε");
      loadOrders();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Σφάλμα");
    }
  };

  return (
    <div data-testid="admin-page" className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10">
      <h1 className="font-serif text-4xl mb-2 text-[#2C1E16] dark:text-[#FFFFFF]">{t("admin.title")}</h1>
      <p className="text-sm text-[#736B66] dark:text-[#B3B3B3] mb-8">Signed in as {user.email}</p>

      <div className="flex gap-6 border-b border-[#EBE5DF] mb-8">
        <button onClick={() => setTab("products")} className={`pb-3 text-sm tracking-widest uppercase font-semibold ${tab === "products" ? "border-b-2 border-[#D4AF37] text-[#2C1E16]" : "text-[#736B66]"}`} data-testid="tab-products">
          {t("admin.products")} ({products.length})
        </button>
        <button onClick={() => setTab("orders")} className={`pb-3 text-sm tracking-widest uppercase font-semibold ${tab === "orders" ? "border-b-2 border-[#D4AF37] text-[#2C1E16]" : "text-[#736B66]"}`} data-testid="tab-orders">
          {t("admin.orders")} ({orders.length})
        </button>
        <button onClick={() => setTab("jobs")} className={`pb-3 text-sm tracking-widest uppercase font-semibold ${tab === "jobs" ? "border-b-2 border-[#D4AF37] text-[#2C1E16]" : "text-[#736B66]"}`} data-testid="tab-jobs">
          {t("admin.jobs")}
        </button>
        <button onClick={() => setTab("coupons")} className={`pb-3 text-sm tracking-widest uppercase font-semibold ${tab === "coupons" ? "border-b-2 border-[#D4AF37] text-[#2C1E16]" : "text-[#736B66]"}`} data-testid="tab-coupons">
          {t("admin.coupons")}
        </button>
        <button onClick={() => setTab("categories")} className={`pb-3 text-sm tracking-widest uppercase font-semibold ${tab === "categories" ? "border-b-2 border-[#D4AF37] text-[#2C1E16]" : "text-[#736B66]"}`} data-testid="tab-categories">
          {t("admin.categories")}
        </button>
        <button onClick={() => setTab("integrations")} className={`pb-3 text-sm tracking-widest uppercase font-semibold ${tab === "integrations" ? "border-b-2 border-[#D4AF37] text-[#2C1E16]" : "text-[#736B66]"}`} data-testid="tab-integrations">
          {t("admin.integrations")}
        </button>
        <button onClick={() => setTab("settings")} className={`pb-3 text-sm tracking-widest uppercase font-semibold ${tab === "settings" ? "border-b-2 border-[#D4AF37] text-[#2C1E16]" : "text-[#736B66]"}`} data-testid="tab-settings">
          {t("admin.settings")}
        </button>
        <button onClick={() => setTab("backup")} className={`pb-3 text-sm tracking-widest uppercase font-semibold ${tab === "backup" ? "border-b-2 border-[#D4AF37] text-[#2C1E16]" : "text-[#736B66]"}`} data-testid="tab-backup">
          {t("admin.backup")}
        </button>
      </div>

      {tab === "products" && (
        <div>
          <div className="flex justify-end mb-4">
            <button onClick={() => openEdit(null)} className="luxury-btn" data-testid="add-product-btn"><Plus size={16} /> {t("admin.addProduct")}</button>
          </div>
          <div className="bg-white border border-[#EBE5DF] overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#F5F2ED] text-[#2C1E16]">
                <tr>
                  <th className="text-left px-4 py-3">{t("admin.name")}</th>
                  <th className="text-left px-4 py-3">{t("admin.category")}</th>
                  <th className="text-left px-4 py-3">{t("admin.priceEur")}</th>
                  <th className="text-left px-4 py-3">{t("admin.stock")}</th>
                  <th className="text-left px-4 py-3">Source</th>
                  <th className="text-right px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {products.map((p) => (
                  <tr key={p.id} className="border-t border-[#EBE5DF]" data-testid={`row-${p.id}`}>
                    <td className="px-4 py-3">{p.name_el}</td>
                    <td className="px-4 py-3 text-[#736B66]">{p.category_slug}</td>
                    <td className="px-4 py-3">{p.price.toFixed(2)}€</td>
                    <td className="px-4 py-3">{p.stock}</td>
                    <td className="px-4 py-3 text-[#736B66]">{p.source}</td>
                    <td className="px-4 py-3 text-right">
                      <button onClick={() => openEdit(p)} className="text-[#2C1E16] hover:text-[#D4AF37] mr-3" data-testid={`edit-${p.id}`}><Pencil size={14} /></button>
                      <button onClick={() => remove(p.id)} className="text-[#736B66] hover:text-[#8B0000]" data-testid={`delete-${p.id}`}><Trash2 size={14} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "orders" && (
        <div className="bg-white border border-[#EBE5DF] overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F5F2ED]">
              <tr>
                <th className="text-left px-4 py-3">Order ID</th>
                <th className="text-left px-4 py-3">Email</th>
                <th className="text-left px-4 py-3">Total</th>
                <th className="text-left px-4 py-3">Payment</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-left px-4 py-3">Date</th>
                <th className="text-right px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((o) => (
                <tr key={o.id} className="border-t border-[#EBE5DF] hover:bg-[#FAF7EE] transition-colors">
                  <td className="px-4 py-3 font-mono text-xs">
                    <button
                      onClick={() => setOpenOrderId(o.id)}
                      className="text-[#D4AF37] hover:text-[#2C1E16] underline-offset-2 hover:underline"
                      data-testid={`open-order-${o.id}`}
                    >
                      {o.id}
                    </button>
                  </td>
                  <td className="px-4 py-3">{o.customer_email || "—"}</td>
                  <td className="px-4 py-3">{o.total.toFixed(2)}€</td>
                  <td className="px-4 py-3">{o.payment_method}</td>
                  <td className="px-4 py-3">
                    <span className="text-xs uppercase tracking-widest font-semibold">{o.order_status} / {o.payment_status}</span>
                  </td>
                  <td className="px-4 py-3 text-[#736B66]">{new Date(o.created_at).toLocaleString()}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex items-center gap-1">
                      {o.order_status !== "cancelled" && (
                        <button
                          onClick={() => cancelOrder(o.id)}
                          title="Ακύρωση παραγγελίας"
                          className="px-2 py-1 text-xs text-[#8B0000] hover:bg-[#8B0000]/10 rounded transition-colors"
                          data-testid={`cancel-order-${o.id}`}
                        >
                          Ακύρωση
                        </button>
                      )}
                      <button
                        onClick={() => deleteOrder(o.id)}
                        title="Διαγραφή παραγγελίας"
                        className="p-1.5 text-[#8B0000] hover:bg-[#8B0000]/10 rounded transition-colors"
                        data-testid={`delete-order-${o.id}`}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {orders.length === 0 && (
                <tr><td colSpan={7} className="text-center py-10 text-[#736B66]">No orders yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {tab === "jobs" && <AdminJobs />}
      {tab === "coupons" && <AdminCoupons />}
      {tab === "categories" && <AdminCategories />}
      {tab === "integrations" && <AdminIntegrations />}

      {tab === "settings" && (
        <div className="space-y-10" data-testid="settings-panel">
          <AdminVisitorStats />

          <AdminNotifications />

          <div className="max-w-2xl">
            <div className="bg-white border border-[#EBE5DF] p-8">
            <div className="flex items-start justify-between gap-6 mb-4">
              <div className="flex-1">
                <h3 className="font-serif text-2xl mb-2">{t("admin.maintenanceMode")}</h3>
                <p className="text-sm text-[#736B66]">{t("admin.maintenanceDesc")}</p>
              </div>
              <span
                data-testid="maintenance-status"
                className={`text-xs tracking-widest uppercase font-semibold px-3 py-1 rounded ${
                  maintenance ? "bg-[#8B0000]/10 text-[#8B0000]" : "bg-[#D4AF37]/10 text-[#7A6017]"
                }`}
              >
                {maintenance ? t("admin.maintenanceOn") : t("admin.maintenanceOff")}
              </span>
            </div>
            <button
              onClick={toggleMaintenance}
              disabled={maintLoading}
              className={maintenance ? "luxury-btn-outline" : "luxury-btn"}
              data-testid="toggle-maintenance"
            >
              {maintLoading
                ? "..."
                : maintenance
                ? t("admin.disable")
                : t("admin.enable")}
            </button>
            </div>
          </div>
        </div>
      )}

      {tab === "backup" && <AdminBackup />}

      <OrderDetailModal orderId={openOrderId} onClose={() => setOpenOrderId(null)} />

      {/* Edit Modal */}
      {editing && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={closeEdit}>
          <div className="bg-white max-w-3xl w-full max-h-[90vh] overflow-y-auto p-8" onClick={(e) => e.stopPropagation()} data-testid="product-form">
            <div className="flex justify-between items-center mb-6">
              <h2 className="font-serif text-2xl">{editing === "new" ? t("admin.addProduct") : t("admin.editProduct")}</h2>
              <button onClick={closeEdit}><X size={20} /></button>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="overline mb-1 block">{t("admin.nameEl")}</label>
                <input className="luxury-input-box" value={formData.name_el} onChange={onChange("name_el")} data-testid="form-name-el" />
              </div>
              <div>
                <label className="overline mb-1 block">{t("admin.nameEn")}</label>
                <input className="luxury-input-box" value={formData.name_en} onChange={onChange("name_en")} data-testid="form-name-en" />
              </div>
              <div className="sm:col-span-2">
                <label className="overline mb-1 block">{t("admin.descEl")}</label>
                <textarea rows={3} className="luxury-input-box" value={formData.description_el} onChange={onChange("description_el")} />
              </div>
              <div className="sm:col-span-2">
                <label className="overline mb-1 block">{t("admin.descEn")}</label>
                <textarea rows={3} className="luxury-input-box" value={formData.description_en} onChange={onChange("description_en")} />
              </div>
              <div>
                <label className="overline mb-1 block">{t("admin.priceEur")}</label>
                <input type="number" step="0.01" className="luxury-input-box" value={formData.price} onChange={onChange("price")} data-testid="form-price" />
                {formData.source !== "custom" && formData.cost_price != null && (
                  <p className="text-[10px] text-[#736B66] mt-1">
                    Platform cost: {Number(formData.cost_price).toFixed(2)}€
                  </p>
                )}
              </div>
              <div>
                <label className="overline mb-1 block">{t("admin.salePrice")}</label>
                <input type="number" step="0.01" className="luxury-input-box" value={formData.sale_price || ""} onChange={onChange("sale_price")} />
              </div>
              {formData.source !== "custom" && (
                <div>
                  <label className="overline mb-1 block">
                    {t("admin.markupPercent")}
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      step="1"
                      min="0"
                      className="luxury-input-box flex-1"
                      placeholder="π.χ. 30"
                      value={formData.markup_percent ?? ""}
                      onChange={onChange("markup_percent")}
                      data-testid="form-markup"
                    />
                    <span className="text-sm text-[#736B66]">%</span>
                  </div>
                  <p className="text-[10px] text-[#736B66] mt-1">
                    Αυτόματος υπολογισμός: cost × (1 + markup/100)
                  </p>
                </div>
              )}
              <div>
                <label className="overline mb-1 block">{t("admin.category")}</label>
                <select className="luxury-input-box" value={formData.category_slug} onChange={onChange("category_slug")}>
                  {categories.map((c) => <option key={c.id} value={c.slug}>{c.name_el}</option>)}
                </select>
              </div>
              <div>
                <label className="overline mb-1 block">{t("admin.stock")}</label>
                <input type="number" className="luxury-input-box" value={formData.stock} onChange={onChange("stock")} />
              </div>
              <div>
                <label className="overline mb-1 block">{t("admin.brand")}</label>
                <input className="luxury-input-box" value={formData.brand || ""} onChange={onChange("brand")} />
              </div>
              <div>
                <label className="overline mb-1 block">{t("admin.sku")}</label>
                <input className="luxury-input-box" value={formData.sku || ""} onChange={onChange("sku")} />
              </div>
              <div>
                <label className="overline mb-1 block">{t("admin.source")}</label>
                <select className="luxury-input-box" value={formData.source} onChange={onChange("source")}>
                  <option value="custom">custom</option>
                  <option value="shopify">shopify</option>
                  <option value="bigbuy">bigbuy</option>
                </select>
              </div>
              <div>
                <label className="overline mb-1 block">External ID</label>
                <input className="luxury-input-box" value={formData.external_id || ""} onChange={onChange("external_id")} />
              </div>
              <div className="sm:col-span-2">
                <label className="overline mb-2 block">{t("admin.images")}</label>
                <ImageUploader
                  value={Array.isArray(formData.images) ? formData.images : (formData.images ? String(formData.images).split(",").map(s => s.trim()).filter(Boolean) : [])}
                  onChange={(arr) => setFormData((s) => ({ ...s, images: arr }))}
                  max={8}
                />
              </div>
              <div className="sm:col-span-2 flex items-center gap-2">
                <input type="checkbox" id="featured" checked={!!formData.featured} onChange={onChange("featured")} />
                <label htmlFor="featured" className="text-sm">{t("admin.featured")}</label>
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-8">
              <button onClick={closeEdit} className="luxury-btn-outline">{t("admin.cancel")}</button>
              <button onClick={save} className="luxury-btn" data-testid="save-product">{t("admin.save")}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Admin;
